// src/views/Admin/Locales/LocalPage/components/SessionsTable/SessionsTable.tsx

import React, { useState } from 'react';
import { HiOutlineCalendarDays, HiOutlineChevronLeft, HiOutlineChevronRight, HiOutlineChevronDown } from 'react-icons/hi2';
import { FiClock, FiDollarSign, FiFileText } from 'react-icons/fi';
import './SessionsTable.scss';

interface Session {
  session_id: number;
  start_time: string;
  end_time?: string;
  total_sales?: number;
  total_tickets?: number;
  user_name?: string;
}

interface SessionsTableProps {
  sessions: Session[];
  title?: string;
}

const SessionsTable: React.FC<SessionsTableProps> = ({ 
  sessions, 
  title = 'Sesiones Recientes' 
}) => {
  const [sessionstableCollapsed, setSessionstableCollapsed] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 5;

  const formatDate = (dateStr?: string) => {
    if (!dateStr) return 'N/A';
    return new Date(dateStr).toLocaleDateString('es-CR', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    });
  };

  const formatTime = (dateStr?: string) => {
    if (!dateStr) return 'N/A';
    return new Date(dateStr).toLocaleTimeString('es-CR', {
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatCurrency = (value?: number) => {
    if (value === undefined || value === null) return '$0';
    return new Intl.NumberFormat('es-CL', {
      style: 'currency',
      currency: 'CLP',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(value);
  };

  const calculateDuration = (start?: string, end?: string) => {
    if (!start || !end) return 'N/A';
    const startDate = new Date(start);
    const endDate = new Date(end);
    const diffMs = endDate.getTime() - startDate.getTime();
    const hours = Math.floor(diffMs / (1000 * 60 * 60));
    const minutes = Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60));
    return `${hours}h ${minutes}m`;
  };

  const totalPages = Math.ceil(sessions.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const paginatedSessions = sessions.slice(startIndex, startIndex + itemsPerPage);

  return (
    <div className="sessions-table-card">
      <div className="sessions-table-header">
        <div className="header-content">
          <button className="sessionstable-collapse-toggle" onClick={() => setSessionstableCollapsed(!sessionstableCollapsed)}>
            {sessionstableCollapsed ? <HiOutlineChevronRight size={20} /> : <HiOutlineChevronDown size={20} />}
          </button>
          <HiOutlineCalendarDays size={20} />
          <h3>{title}</h3>
          <span className="session-count">{sessions.length} sesiones</span>
        </div>
      </div>

      {!sessionstableCollapsed && (
      <div className="sessions-table-body">
        {sessions.length === 0 ? (
          <div className="no-sessions">
            <HiOutlineCalendarDays size={48} />
            <p>No hay sesiones registradas</p>
          </div>
        ) : (
          <>
            <div className="table-container">
              <div className="table-header-row">
                <span>Fecha</span>
                <span>Hora</span>
                <span>Duración</span>
                <span>Ventas</span>
                <span>Boletas</span>
              </div>
              
              {paginatedSessions.map((session) => (
                <div key={session.session_id} className="table-row">
                  <span className="cell date">
                    <HiOutlineCalendarDays size={14} />
                    {formatDate(session.start_time)}
                  </span>
                  <span className="cell time">
                    <FiClock size={14} />
                    {formatTime(session.start_time)}
                  </span>
                  <span className="cell duration">
                    {calculateDuration(session.start_time, session.end_time)}
                  </span>
                  <span className="cell sales">
                    <FiDollarSign size={14} />
                    {formatCurrency(session.total_sales)}
                  </span>
                  <span className="cell tickets">
                    <FiFileText size={14} />
                    {session.total_tickets ?? 0}
                  </span>
                </div>
              ))}
            </div>

            {totalPages > 1 && (
              <div className="pagination">
                <button 
                  onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                  disabled={currentPage === 1}
                >
                  <HiOutlineChevronLeft size={16} />
                </button>
                <span className="page-info">
                  Página {currentPage} de {totalPages}
                </span>
                <button 
                  onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                  disabled={currentPage === totalPages}
                >
                  <HiOutlineChevronRight size={16} />
                </button>
              </div>
            )}
          </>
        )}
      </div>
      )}
    </div>
  );
};

export default SessionsTable;
